'use client';

import { useEffect, useRef, useState } from 'react';

type TodoItem = {
  id: string;
  text: string;
  done: boolean;
};

const STORAGE_KEY = 'cleanor-todo-list-v1';

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function BrowserTodoList() {
  const [items, setItems] = useState<TodoItem[]>([]);
  const [draft, setDraft] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const skipFirstSave = useRef(true);

  // Load from localStorage after mount so the server render stays stable.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    let cancelled = false;
    async function load() {
      // Defer past the synchronous render so seeding never blocks hydration.
      await Promise.resolve();
      if (cancelled) {
        return;
      }
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as TodoItem[];
          if (Array.isArray(parsed)) {
            setItems(
              parsed.filter(
                (item): item is TodoItem =>
                  typeof item?.id === 'string' &&
                  typeof item?.text === 'string' &&
                  typeof item?.done === 'boolean',
              ),
            );
          }
        }
      } catch {
        // Ignore malformed storage and start empty.
      }
      setHydrated(true);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist after hydration; skip the first run so we never overwrite before load.
  useEffect(() => {
    if (!hydrated || typeof window === 'undefined') {
      return;
    }
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Ignore quota or serialization errors.
    }
  }, [items, hydrated]);

  function addItem() {
    const text = draft.trim();
    if (!text) {
      return;
    }
    setItems((prev) => [{ id: createId(), text, done: false }, ...prev]);
    setDraft('');
  }

  function toggleItem(id: string) {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, done: !item.done } : item)));
  }

  function deleteItem(id: string) {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }

  function clearCompleted() {
    setItems((prev) => prev.filter((item) => !item.done));
  }

  const remaining = items.filter((item) => !item.done).length;
  const completed = items.length - remaining;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">To-Do List</span>
        <h2 className="tool-live-title">Online to-do list</h2>
        <p className="tool-live-subtitle">
          A simple checklist that saves itself in your browser. Add tasks, check them off, and clear
          the ones you have finished. Your list survives a reload and never leaves this device.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>New task</span>
              <input
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addItem();
                  }
                }}
                placeholder="What needs doing?"
                type="text"
                value={draft}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={addItem} type="button">
              Add task
            </button>
            <button
              className="button button-secondary"
              disabled={completed === 0}
              onClick={clearCompleted}
              type="button"
            >
              Clear completed
            </button>
          </div>
          <p className="tool-live-panel-note">
            {items.length === 0
              ? 'No tasks yet. Add one above to get started.'
              : `${remaining} to do, ${completed} done.`}
          </p>
        </section>

        <section className="tool-live-preview">
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
            {items.map((item) => (
              <li
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  padding: '0.6rem 0.75rem',
                  borderRadius: '0.6rem',
                  background: 'rgba(148, 163, 184, 0.12)',
                }}
              >
                <input
                  aria-label={item.done ? 'Mark as not done' : 'Mark as done'}
                  checked={item.done}
                  onChange={() => toggleItem(item.id)}
                  style={{ width: '1.15rem', height: '1.15rem', flexShrink: 0 }}
                  type="checkbox"
                />
                <span
                  style={{
                    flex: 1,
                    textDecoration: item.done ? 'line-through' : 'none',
                    opacity: item.done ? 0.55 : 1,
                    wordBreak: 'break-word',
                  }}
                >
                  {item.text}
                </span>
                <button
                  aria-label="Delete task"
                  className="button button-secondary"
                  onClick={() => deleteItem(item.id)}
                  style={{ padding: '0.25rem 0.6rem', flexShrink: 0 }}
                  type="button"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
          {items.length === 0 ? (
            <p className="tool-live-panel-note">Your tasks will appear here.</p>
          ) : null}
        </section>
      </div>
    </div>
  );
}
